<template>
  <ion-page>
    <ion-content color="primary">
      <div v-if="!isLoading" class="ion-padding h-100 w-100 d-flex justify-space-center">
        <div
          v-if="!isLocationDisabled && isAvailable"
          class="content w-100 d-flex align-center justify-space-end h-100 flex-column"
        >
          <swiper @swiper="onSwiper">
            <swiper-slide v-if="isPublic && !ownerId" class="swiper-no-swiping">
              <div class="w-100 d-flex flex-column h-100 register-card">
                <GuestFormCheck
                  :booking-id="bookingId"
                  @continue="handleSlideTo(selectedSwiper + 1)"
                />
              </div>
            </swiper-slide>
            <swiper-slide class="swiper-no-swiping"
              ><div class="w-100 d-flex flex-column h-100 steps-cards">
                <CheckSteps :booking-id="bookingId" :type="'check-out'" :user="getUser" />
              </div>
            </swiper-slide>
          </swiper>
        </div>

        <div
          v-else-if="isLocationDisabled"
          class="content w-100 d-flex align-center justify-space-end h-100 flex-column"
        >
          <RequestPermissionsPage />
        </div>

        <div
          v-else-if="!isAvailable"
          class="content w-100 d-flex align-center justify-space-end h-100 flex-column"
        >
          <CheckoutNotAvailable />
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>
<script setup lang="ts">
  import { Swiper, SwiperSlide } from 'swiper/vue';
  import { IonPage, IonContent } from '@ionic/vue';
  import { useElasticStore } from '@/stores/elastic-store';
  import { computed, onMounted, ref } from 'vue';
  import { useToast } from '@/composables/useToast';
  import { storeToRefs } from 'pinia';
  import { useCheckInStore } from '@/stores/checkin-store';
  import CheckSteps from '@/components/check/CheckSteps.vue';
  import { getCurrentPosition } from '@/plugins/geolocalization';
  import RequestPermissionsPage from '@/components/check/RequestPermissionsPage.vue';
  import { useRoute } from 'vue-router';
  import { User } from '@/interfaces/auth/auth-store.interface';
  import GuestFormCheck from '@/components/check/GuestFormCheck.vue';
  import CheckoutNotAvailable from '@/components/check/CheckoutNotAvailable.vue';

  const { rentalUnit, coords, evaluator, isPublic, ownerId } = storeToRefs(useCheckInStore());
  const { getRentalUnitPreview } = useElasticStore();
  const { getQuestions, getIsAvailableCheckOut } = useCheckInStore();
  const { showToast } = useToast();

  const isLoading = ref(true);
  const isLocationDisabled = ref(false);
  const isAvailable = ref(true);
  const route = useRoute();
  const bookingId = ref('');
  isPublic.value = !!route.query.bookingId;
  ownerId.value = (route.query.ownerId as string) ?? null;
  const swiperInstance = ref();
  const selectedSwiper = ref(0);

  const getUser = computed(() => {
    return isPublic.value && rentalUnit.value && rentalUnit.value.Owner
      ? rentalUnit.value.Owner
      : (evaluator.value as User);
  });

  const handleSlideTo = (idx: number) => {
    if (idx >= 0) {
      selectedSwiper.value = idx;
      swiperInstance.value?.slideTo(selectedSwiper.value);
    }
  };

  const handleGetRentalPreview = async (id: string) => {
    try {
      const response = await getRentalUnitPreview(id);
      if (response.id) {
        rentalUnit.value = response;
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const verifyIsAvailable = async (bookingId: string) => {
    try {
      const response = await getIsAvailableCheckOut(bookingId);
      if (response) {
        await getInitialData();
      } else {
        isAvailable.value = false;
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const getInitialData = async () => {
    const rentalId = route.query.rentalId ?? (route.params.rentalId as string);

    if (rentalId) {
      await handleGetRentalPreview(rentalId as string);
      await getQuestions();
    } else {
      isAvailable.value = false;
    }
  };

  const initialConfig = async () => {
    isLoading.value = true;
    bookingId.value = (route.query.bookingId as string) ?? (route.params.bookingId as string);

    if (isPublic.value) {
      await verifyIsAvailable(bookingId.value);
    } else {
      await getInitialData();
    }

    isLoading.value = false;
  };

  const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
    swiperInstance.value?.slideTo(selectedSwiper.value);
  };

  onMounted(async () => {
    const position = await getCurrentPosition();

    if (position) {
      initialConfig();
      coords.value = position;
    } else {
      isLocationDisabled.value = true;
      isLoading.value = false;
    }
  });
</script>
<style lang="css" scoped>
  .swiper {
    width: 100% !important;
    height: 100%;
  }

  .register-card {
    max-width: 400px;
    justify-content: end;
  }

  .steps-cards {
    max-width: 600px;
    justify-content: end;
  }

  @media (min-width: 500px) {
    .register-card {
      justify-content: center;
    }
  }
</style>
